/**
 * Photo edits in the browser — the Studio Editor's result editor (light balance, brightness, exposure, contrast, highlights, shadows,
 * saturation, sharpness, vignette, a preset filter with a strength, rotate / flip). One pixel pipeline drives both the live preview
 * (on a downscaled copy) and the full-size bake for the instant download / print / copy, so what is seen is what leaves.
 *
 * Nothing here is saved: the edits live in page state and are burnt into a fresh Blob only when the picture leaves the page.
 * Hand it the picture's BYTES (a Blob from the backend proxy) — the R2 files host sends no CORS headers, so a URL would taint the canvas.
 */
import { useEffect, useRef, useState } from 'react';

// The Adjust tools, in the order the drawer shows them. Every value is 0 when untouched.
export const EDIT_TOOLS = [
    { key: 'warmth', min: -100, max: 100 },
    { key: 'brightness', min: -100, max: 100 },
    { key: 'exposure', min: -100, max: 100 },
    { key: 'contrast', min: -100, max: 100 },
    { key: 'highlights', min: -100, max: 100 },
    { key: 'shadows', min: -100, max: 100 },
    { key: 'saturation', min: -100, max: 100 },
    { key: 'sharpness', min: 0, max: 100 },
    { key: 'vignette', min: 0, max: 100 },
];

// Preset looks — each is a set of tool values (plus `fade`, a lifted-black matte only presets use), scaled by the filter strength and
// added on top of the user's own tool values.
export const FILTER_PRESETS = [
    { id: 'none', adj: {} },
    { id: 'natural', adj: { contrast: 8, saturation: 8, highlights: -6, shadows: 6 } },
    { id: 'vivid', adj: { contrast: 16, saturation: 32, highlights: -8 } },
    { id: 'studio', adj: { exposure: 8, contrast: 12, highlights: -14, shadows: 12, sharpness: 15 } },
    { id: 'bright', adj: { brightness: 16, shadows: 18, highlights: -6 } },
    { id: 'warm', adj: { warmth: 32, saturation: 6 } },
    { id: 'cool', adj: { warmth: -30, contrast: 6 } },
    { id: 'soft', adj: { contrast: -12, highlights: -16, brightness: 6, fade: 10 } },
    { id: 'matte', adj: { contrast: -14, shadows: 20, saturation: -10, fade: 30 } },
    { id: 'dramatic', adj: { contrast: 32, highlights: -22, shadows: -16, saturation: -8, vignette: 30 } },
    { id: 'vintage', adj: { warmth: 22, saturation: -28, contrast: -6, fade: 26, vignette: 28 } },
    { id: 'bw', adj: { saturation: -100, contrast: 14 } },
];

export const NEUTRAL_EDITS = {
    warmth: 0, brightness: 0, exposure: 0, contrast: 0, highlights: 0, shadows: 0, saturation: 0, sharpness: 0, vignette: 0,
    filter: 'none', filterStrength: 100, rotate: 0, flipH: false,
};

/** Any edit that changes the picture (tools, a filter, rotation or a flip)? */
export function isEdited(e) {
    if (!e) return false;
    if (EDIT_TOOLS.some((t) => (e[t.key] || 0) !== 0)) return true;
    return (e.filter && e.filter !== 'none' && e.filterStrength > 0) || (e.rotate || 0) % 360 !== 0 || !!e.flipH;
}

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
const smooth = (a, b, x) => { const t = clamp((x - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };

/** The user's tool values + the filter (scaled by its strength) → the numbers the pipeline uses. */
function resolve(e) {
    const preset = FILTER_PRESETS.find((p) => p.id === e.filter)?.adj || {};
    const k = clamp((e.filterStrength ?? 100) / 100, 0, 1);
    const v = (key, lo = -100, hi = 100) => clamp((e[key] || 0) + (preset[key] || 0) * k, lo, hi);
    return {
        warmth: v('warmth'), brightness: v('brightness'), exposure: v('exposure'), contrast: v('contrast'),
        highlights: v('highlights'), shadows: v('shadows'), saturation: v('saturation'),
        sharpness: v('sharpness', 0, 100), vignette: v('vignette', 0, 100), fade: clamp((preset.fade || 0) * k, 0, 100),
    };
}

// The tone curve (exposure → brightness → contrast → highlights / shadows → fade), per channel, as a 256-entry table.
function toneLut(p) {
    const lut = new Uint8ClampedArray(256);
    const expo = 2 ** (p.exposure / 100);          // ±1 stop
    const gamma = 2 ** (-p.brightness / 100 * 0.8); // brightness bends the mid-tones, ends stay put
    const contrast = 2 ** (p.contrast / 100);       // ×0.5 … ×2 around the middle grey
    const hi = p.highlights / 100 * 0.28;
    const sh = p.shadows / 100 * 0.28;
    const fade = p.fade / 100;
    for (let i = 0; i < 256; i += 1) {
        let v = (i / 255) * expo;
        v = clamp(v, 0, 1) ** gamma;
        v = (v - 0.5) * contrast + 0.5;
        v += hi * smooth(0.45, 1, v) + sh * (1 - smooth(0, 0.55, v));
        v = v * (1 - fade * 0.22) + fade * 0.1;
        lut[i] = clamp(Math.round(v * 255), 0, 255);
    }
    return lut;
}

/** Apply the colour / tone / sharpen / vignette edits to a canvas's pixels in place. */
function applyPixels(ctx, w, h, p) {
    const img = ctx.getImageData(0, 0, w, h);
    const d = img.data;
    const lut = toneLut(p);
    const sat = 1 + p.saturation / 100;               // 0 = grey … 2 = doubled
    const wr = 1 + p.warmth / 100 * 0.14;
    const wg = 1 + p.warmth / 100 * 0.03;
    const wb = 1 - p.warmth / 100 * 0.14;
    const vig = p.vignette / 100 * 0.65;
    const cx = w / 2;
    const cy = h / 2;
    const maxD = Math.hypot(cx, cy) || 1;
    const touchColour = sat !== 1 || p.warmth !== 0;
    for (let y = 0, i = 0; y < h; y += 1) {
        for (let x = 0; x < w; x += 1, i += 4) {
            let r = lut[d[i]];
            let g = lut[d[i + 1]];
            let b = lut[d[i + 2]];
            if (touchColour) {
                r *= wr; g *= wg; b *= wb;
                const l = 0.2126 * r + 0.7152 * g + 0.0722 * b;
                r = l + (r - l) * sat; g = l + (g - l) * sat; b = l + (b - l) * sat;
            }
            if (vig) {
                const f = 1 - vig * smooth(0.4, 1, Math.hypot(x - cx, y - cy) / maxD);
                r *= f; g *= f; b *= f;
            }
            d[i] = r; d[i + 1] = g; d[i + 2] = b; // Uint8ClampedArray clamps and rounds
        }
    }
    if (p.sharpness > 0) sharpen(d, w, h, p.sharpness / 100);
    ctx.putImageData(img, 0, 0);
}

// 3×3 unsharp kernel (centre 1+4k, cross −k) — the same shape as the AI editor's sharpen.
function sharpen(d, w, h, k) {
    const src = new Uint8ClampedArray(d);
    const c = 1 + 4 * k;
    const row = w * 4;
    for (let y = 1; y < h - 1; y += 1) {
        for (let x = 1; x < w - 1; x += 1) {
            const i = (y * w + x) * 4;
            for (let ch = 0; ch < 3; ch += 1) {
                const j = i + ch;
                d[j] = src[j] * c - k * (src[j - 4] + src[j + 4] + src[j - row] + src[j + row]);
            }
        }
    }
}

/** Draw `source` (an ImageBitmap / image / canvas) rotated / flipped and scaled to fit `maxDim`, then apply the edits. */
export function renderEdits(source, edits, maxDim = Infinity) {
    const e = { ...NEUTRAL_EDITS, ...edits };
    const sw = source.width || source.naturalWidth;
    const sh = source.height || source.naturalHeight;
    const s = Math.min(1, maxDim / Math.max(sw, sh));
    const w = Math.max(1, Math.round(sw * s));
    const h = Math.max(1, Math.round(sh * s));
    const turns = (((Math.round((e.rotate || 0) / 90)) % 4) + 4) % 4;
    const canvas = document.createElement('canvas');
    canvas.width = turns % 2 ? h : w;
    canvas.height = turns % 2 ? w : h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.imageSmoothingQuality = 'high';
    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((turns * Math.PI) / 2);
    if (e.flipH) ctx.scale(-1, 1);
    ctx.drawImage(source, -w / 2, -h / 2, w, h);
    ctx.restore();
    applyPixels(ctx, canvas.width, canvas.height, resolve(e));
    return canvas;
}

const toBlob = (canvas, type, q) => new Promise((res, rej) => canvas.toBlob((b) => (b ? res(b) : rej(new Error('image export failed'))), type, q));

/**
 * Burn the edits into the full-size picture. Throws when it cannot be read or redrawn — callers must tell the user, never quietly hand
 * over the unedited file.
 */
export async function bakePhotoEdits(blob, edits, { type } = {}) {
    const bitmap = await createImageBitmap(blob);
    try {
        const canvas = renderEdits(bitmap, edits);
        const outType = type || (/png|webp/.test(blob.type) ? 'image/png' : 'image/jpeg');
        const out = await toBlob(canvas, outType, 0.95);
        canvas.width = 0; canvas.height = 0;
        return out;
    } finally {
        bitmap.close?.();
    }
}

/**
 * Live preview: the edited picture as a blob: URL, re-rendered (on a copy at most `maxDim` px) whenever the edits change — null while
 * nothing is edited, so the caller shows the original. Renders are coalesced: a slider drag only ever renders the latest value.
 */
export function usePhotoEditPreview(sourceBlob, edits, maxDim = 1100) {
    const [url, setUrl] = useState(null);
    const baseRef = useRef(null);
    const pendingRef = useRef(null);
    const busyRef = useRef(false);
    const urlRef = useRef(null);
    const [baseReady, setBaseReady] = useState(0);

    const swapUrl = (next) => {
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = next;
        setUrl(next);
    };

    // A downscaled copy of the source, decoded once.
    useEffect(() => {
        let alive = true;
        baseRef.current = null;
        if (!sourceBlob) { swapUrl(null); return undefined; }
        createImageBitmap(sourceBlob).then((bmp) => {
            const s = Math.min(1, maxDim / Math.max(bmp.width, bmp.height));
            // (drawn on a transparent canvas, so a see-through source stays see-through)
            const c = document.createElement('canvas');
            c.width = Math.round(bmp.width * s); c.height = Math.round(bmp.height * s);
            c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
            bmp.close?.();
            if (alive) { baseRef.current = c; setBaseReady((n) => n + 1); }
        }).catch(() => {});
        return () => { alive = false; };
    }, [sourceBlob, maxDim]);

    useEffect(() => {
        if (!baseRef.current || !isEdited(edits)) { if (!isEdited(edits)) swapUrl(null); return; }
        pendingRef.current = edits;
        if (busyRef.current) return;
        const run = async () => {
            busyRef.current = true;
            while (pendingRef.current && baseRef.current) {
                const job = pendingRef.current;
                pendingRef.current = null;
                await new Promise((r) => requestAnimationFrame(() => r()));
                const canvas = renderEdits(baseRef.current, job);
                // a source that can carry transparency (a transparent AI result) keeps its alpha in the preview
                const alpha = /png|webp/.test(sourceBlob?.type || '');
                const blob = await toBlob(canvas, alpha ? 'image/webp' : 'image/jpeg', 0.9).catch(() => null);
                if (blob && isEdited(job)) swapUrl(URL.createObjectURL(blob));
            }
            busyRef.current = false;
        };
        run();
    }, [edits, baseReady]);

    useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);
    return url;
}

/** Small thumbnails of the source under each filter preset (data URLs, keyed by preset id) for the Filters tab. */
export function useFilterThumbs(sourceBlob, size = 96) {
    const [thumbs, setThumbs] = useState({});
    useEffect(() => {
        let alive = true;
        setThumbs({});
        if (!sourceBlob) return undefined;
        createImageBitmap(sourceBlob).then((bmp) => {
            const out = {};
            FILTER_PRESETS.forEach((p) => {
                out[p.id] = renderEdits(bmp, { ...NEUTRAL_EDITS, filter: p.id }, size).toDataURL('image/jpeg', 0.8);
            });
            bmp.close?.();
            if (alive) setThumbs(out);
        }).catch(() => {});
        return () => { alive = false; };
    }, [sourceBlob, size]);
    return thumbs;
}
